import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRemoteConfig, updateRemoteConfig } from '../remote.config.service';
import { IRemoteConfig } from '../remote.config.interfaces';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';

// Mapping from config keys to human-readable names
const configLabels: Record<keyof IRemoteConfig, string> = {
    areSubscriptionsEnabled: 'Subscriptions Enabled',
    areUserSelectionsEnabled: 'User Selections Enabled',
    isAIRecipesEnabled: 'AI Recipes Enabled',
    isAppKeyResultEnabled: 'App Key Result Enabled',
    isGrillAnalyticsEnabled: 'Grill Analytics Enabled',
    isProductSelectionActive: 'Product Selection Active',
    isRedeemCodeEnabled: 'Redeem Code Enabled',
    isSegmentEventTrackingEnabled: 'Segment Event Tracking Enabled',
    isUserOfferEnabled: 'User Offer Enabled',
    showRateUsBanner: 'Show Rate Us Banner',
    useOldSliderStyle: 'Use Old Slider Style',
    isFirebaseEventTrackingEnabled: 'Firebase Event Tracking Enabled',
    isCommunityEnabled: 'Community Enabled',
    isSubscriptionPurchaseEnabled: 'Subscription Purchase Enabled',
    isThreeItemsNavEnabled: 'Three Items Nav Enabled',
    areProductsEnabled: 'Products Enabled',
    areRecipeRatingAndReviewEnabled: 'Recipe Rating and Review Enabled',
    isOneSignalEnabled: 'OneSignal Enabled',
    isFreeSubscriptionForAllEnabled: 'Free Subscription For All Enabled',
};

const configEnabledState: Record<keyof IRemoteConfig, boolean> = {
    areSubscriptionsEnabled: false,
    areUserSelectionsEnabled: false,
    isAIRecipesEnabled: false,
    isAppKeyResultEnabled: false,
    isGrillAnalyticsEnabled: false,
    isProductSelectionActive: false,
    isRedeemCodeEnabled: false,
    isSegmentEventTrackingEnabled: true,
    isUserOfferEnabled: false,
    showRateUsBanner: false,
    useOldSliderStyle: false,
    isFirebaseEventTrackingEnabled: false,
    isCommunityEnabled: true,
    isSubscriptionPurchaseEnabled: false,
    isThreeItemsNavEnabled: true,
    areProductsEnabled: false,
    areRecipeRatingAndReviewEnabled: true,
    isOneSignalEnabled: false,
    isFreeSubscriptionForAllEnabled: false,
};

const configComments: Record<keyof IRemoteConfig, string> = {
    areSubscriptionsEnabled:
        'This one controls some premium features. Since we are offering a free subscription starting from October 2025, this has to stay enabled.',
    areUserSelectionsEnabled: 'Deprecated feature flag for a long time. Always enabled.',
    isAIRecipesEnabled: 'Deprecated feature flag for a long time. Always enabled.',
    isAppKeyResultEnabled: 'Deprecated feature flag for a long time. Always enabled.',
    isGrillAnalyticsEnabled: 'Deprecated feature flag for a long time. Always enabled.',
    isProductSelectionActive: 'No more products since October 2025. Always disabled',
    isRedeemCodeEnabled: 'No more codes. Always disabled',
    isSegmentEventTrackingEnabled: '',
    isUserOfferEnabled: 'No more user offers. Always disabled',
    showRateUsBanner: 'Deprecated feature flag for a long time. Always enabled.',
    useOldSliderStyle: 'Aint using old slider anymore. Always disabled.',
    isFirebaseEventTrackingEnabled: 'No more firebase event tracking. Always disabled.',
    isCommunityEnabled: '',
    isSubscriptionPurchaseEnabled: 'No more purchases since October 2025. Always disabled',
    isThreeItemsNavEnabled: '',
    areProductsEnabled: 'No more products since October 2025. Always disabled',
    areRecipeRatingAndReviewEnabled: '',
    isOneSignalEnabled: 'No more one signal. Always disabled',
    isFreeSubscriptionForAllEnabled: 'Always true since October 2025',
};

const useStyles = makeStyles((theme) => ({
    toolbarSpacer: theme.mixins.toolbar,
}));

const RemoteConfig: React.FC = () => {
    const classes = useStyles();
    const queryClient = useQueryClient();
    const [config, setConfig] = useState<IRemoteConfig | null>(null);

    const { data, isLoading, error } = useQuery<IRemoteConfig>({
        queryKey: ['remoteConfig'],
        queryFn: getRemoteConfig,
        onError: () => {
            toast.error('Failed to load remote config');
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateRemoteConfig,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['remoteConfig'] });
            toast.success('Remote config updated successfully');
        },
        onError: () => {
            toast.error('Failed to update remote config');
        },
    });

    useEffect(() => {
        if (data) {
            setConfig(data);
        }
    }, [data]);

    const handleChange = (key: keyof IRemoteConfig, value: string) => {
        if (config) {
            const newConfig = { ...config, [key]: value === 'true' };
            setConfig(newConfig);
            updateMutation.mutate(newConfig);
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <div className={classes.toolbarSpacer} />
                <Typography variant="h4" color="error" gutterBottom>
                    Error loading remote configuration
                </Typography>
                <Typography color="error">
                    {error instanceof Error ? error.message : 'An unknown error occurred'}
                </Typography>
            </Box>
        );
    }

    if (!config) {
        return (
            <Box p={3}>
                <div className={classes.toolbarSpacer} />
                <Typography variant="h4" color="error" gutterBottom>
                    No configuration data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <div className={classes.toolbarSpacer} />
            <Typography variant="h4" gutterBottom>
                Remote Configuration
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Setting</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell>Comment</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.entries(config).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell>{configLabels[key as keyof IRemoteConfig] || key}</TableCell>
                                <TableCell>
                                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                                        <Select
                                            value={value.toString()}
                                            onChange={(e) =>
                                                handleChange(key as keyof IRemoteConfig, e.target.value as string)
                                            }
                                            disabled={!configEnabledState[key as keyof IRemoteConfig]}
                                        >
                                            <MenuItem value="true">True</MenuItem>
                                            <MenuItem value="false">False</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {configComments[key as keyof IRemoteConfig]}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default RemoteConfig;
